<template>
  <div class="profile-page">
    <div class="profile-shell">
      <section class="hero">
        <button class="back-btn" type="button" @click="back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>

        <div class="count-chip">{{ photoCountText }}</div>

        <van-swipe class="hero-swipe" :show-indicators="false" @change="onSwipeChange">
          <van-swipe-item v-for="(image, index) in gallery" :key="`${image}-${index}`">
            <img class="hero-image" :src="image" :alt="profileName" />
          </van-swipe-item>
        </van-swipe>
      </section>

      <section class="detail-card">
        <div class="title-row">
          <div class="title-copy">
            <h1>{{ profileName }}</h1>
            <p>{{ profileMeta }}</p>
          </div>
          <div class="title-side">
            <span class="status-chip">
              <i></i>
              <span>{{ profileTag }}</span>
            </span>
            <button class="pid-chip" type="button" @click="copyProfileId">
              <span>ID <b>{{ publicId }}</b></span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="score-card">
          <div class="score-ring" :style="{ '--score-angle': scoreAngle }">
            <strong>{{ score }}</strong>
            <span>{{ copy.scoreLabel }}</span>
          </div>
          <div class="score-copy">
            <b>{{ certifiedLabel }}</b>
            <p>{{ copy.scoreDesc }}</p>
            <div class="score-meter">
              <i :style="{ width: `${score}%` }"></i>
            </div>
          </div>
        </div>

        <div class="info-list">
          <div
            v-for="item in infoRows"
            :key="item.key"
            class="info-row"
          >
            <span class="info-ico">{{ item.icon }}</span>
            <span class="info-copy">
              <small>{{ item.label }}</small>
              <b>{{ item.value }}</b>
            </span>
          </div>
        </div>

        <div class="about-card">
          <h2>{{ copy.aboutTitle }}</h2>
          <p>{{ aboutText }}</p>
        </div>

        <div class="action-bar">
          <button class="like-btn" type="button" :class="{ on: liked }" @click="toggleFavorite">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path
                d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1 7.8 7.8 7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8z"
              ></path>
            </svg>
          </button>
          <button class="book-btn" type="button" @click="openService">{{ copy.bookNow }}</button>
        </div>
      </section>
    </div>

    <Footer :is_show="true" />

    <van-popup v-model="serviceVisible" class="service-popup" round>
      <div class="service-card">
        <button class="service-close" type="button" @click="serviceVisible = false">×</button>
        <div class="service-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M21.7 3.2 18.5 20c-.2 1.2-.9 1.5-1.9.9l-4.8-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.9 8.9-8c.4-.3-.1-.5-.6-.2L6.1 13.9 1.4 12.4c-1-.3-1-1 .2-1.5L20 3.8c.9-.3 1.8.2 1.7-.6Z"
            ></path>
          </svg>
        </div>
        <h3>{{ kefuTitle }}</h3>
        <p class="service-sub">{{ copy.serviceDesc }}</p>

        <button
          v-for="agent in serviceAgents"
          :key="agent.name"
          class="service-item"
          type="button"
          @click="openAgent(agent.link)"
        >
          <span class="service-item-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M21.7 3.2 18.5 20c-.2 1.2-.9 1.5-1.9.9l-4.8-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.9 8.9-8c.4-.3-.1-.5-.6-.2L6.1 13.9 1.4 12.4c-1-.3-1-1 .2-1.5L20 3.8c.9-.3 1.8.2 1.7-.6Z"
              ></path>
            </svg>
          </span>
          <span class="service-item-copy">
            <b>{{ agent.name }}</b>
            <small>{{ copy.serviceOnline }}</small>
          </span>
          <em>{{ kefuActionText }}</em>
          <span class="service-arrow">›</span>
        </button>

        <button class="later-btn" type="button" @click="serviceVisible = false">
          {{ copy.serviceLater }}
        </button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { getListPayload } from "@/common/apiData";
import { getPageCopy } from "@/common/pageLocale";
import { getKefuContact, openKefuContact } from "@/common/kefu";
import { resolveAddressLabel } from "@/common/xuanfeiProfile";
import Footer from "@/common/Footer.vue";

export default {
  components: {
    Footer,
  },
  data() {
    return {
      detail: null,
      addressLabel: "",
      loading: false,
      currentSlide: 0,
      liked: false,
      serviceVisible: false,
    };
  },
  computed: {
    locale() {
      return (
        (this.$i18n && this.$i18n.locale) ||
        localStorage.getItem("lang") ||
        localStorage.getItem("language") ||
        "zh_cn"
      );
    },
    copy() {
      return getPageCopy(this, "profile");
    },
    baseInfo() {
      return this.$store.getters.getBaseInfo || {};
    },
    ui() {
      return this.baseInfo.ui || {};
    },
    certifiedLabel() {
      return this.ui.certified_label || this.copy.scoreTitle;
    },
    kefuContact() {
      return getKefuContact(this.baseInfo);
    },
    kefuTitle() {
      return this.kefuContact.title || this.copy.serviceTitle;
    },
    kefuActionText() {
      return this.kefuContact.action_text || this.copy.serviceAction;
    },
    routeId() {
      return Number(this.$route.query.id || 0);
    },
    fallbackIndex() {
      return Math.abs(this.routeId || 1) % this.copy.fallbackName.length;
    },
    profileName() {
      if (this.detailName) {
        return this.detailName;
      }
      if (!this.$route.query.adsid && this.$route.query.name) {
        return this.$route.query.name;
      }
      return this.copy.fallbackName[this.fallbackIndex];
    },
    detailName() {
      return this.detail && this.detail.xuanfei_name ? this.detail.xuanfei_name : "";
    },
    profileRole() {
      const height = this.detail && this.detail.height ? String(this.detail.height).trim() : "";
      const bust = this.detail && this.detail.bust ? String(this.detail.bust).trim() : "";
      if (height || bust) {
        return [height, bust].filter(Boolean).join(" · ");
      }
      if (this.$route.query.role) {
        return this.$route.query.role;
      }
      return "";
    },
    profileCity() {
      if (this.detailCity) {
        return this.detailCity;
      }
      if (this.$route.query.city) {
        return this.$route.query.city;
      }
      return "";
    },
    detailCity() {
      return (
        this.addressLabel ||
        (this.detail && (this.detail.address_name || this.detail.city || this.detail.area)) ||
        ""
      );
    },
    profileMeta() {
      return `${this.profileCity} · ${this.profileRole}`;
    },
    profileTag() {
      return this.$route.query.tag || this.copy.bookable;
    },
    publicId() {
      if (this.$route.query.publicId) {
        return this.$route.query.publicId;
      }
      return `96${String(this.routeId || this.fallbackIndex + 1).padStart(6, "0")}`;
    },
    gallery() {
      const images = this.detail && Array.isArray(this.detail.img_url) ? this.detail.img_url : [];
      if (images.length) {
        return images;
      }
      if (this.$route.query.image) {
        return [this.$route.query.image];
      }
      return ["/img/null.png"];
    },
    photoCountText() {
      const count = this.gallery.length;
      return this.locale === "en_us" ? `${count} ${this.copy.photoUnit}` : `${count}${this.copy.photoUnit}`;
    },
    score() {
      const base = 80 + (this.routeId % 21);
      return Math.max(80, Math.min(100, base));
    },
    scoreAngle() {
      return `${Math.round((this.score / 100) * 360)}deg`;
    },
    aboutText() {
      // Product rule:「关于Ta」固定展示审核通过引导文案，不展示接口介绍/姓名。
      return this.copy.aboutDesc || "";
    },
    infoRows() {
      return [
        {
          key: "base",
          icon: "I",
          label: this.copy.baseInfo,
          value: [this.profileName, this.profileRole].filter(Boolean).join(" · ") || "--",
          action: "",
        },
        {
          key: "address",
          icon: "A",
          label: this.copy.address,
          value: this.profileCity || this.copy.addressValue || "--",
          action: "",
        },
        {
          key: "contact",
          icon: "C",
          label: this.copy.contact,
          value: this.copy.contactValue || "--",
          action: "",
        },
        {
          key: "area",
          icon: "R",
          label: this.copy.area,
          value: this.profileCity || "--",
          action: "",
        },
        {
          key: "service",
          icon: "S",
          label: this.copy.service,
          value: this.copy.serviceValue || "--",
          action: "",
        },
      ];
    },
    serviceAgents() {
      const contact = this.kefuContact;
      if (contact.url) {
        return [{ name: contact.name || this.copy.serviceExclusiveName, link: contact.url }];
      }
      return [];
    },
  },
  watch: {
    "$route.query.id": {
      immediate: true,
      handler() {
        this.fetchDetail();
        this.syncLikedState();
      },
    },
  },
  methods: {
    back() {
      this.$router.back();
    },
    onSwipeChange(index) {
      this.currentSlide = index;
    },
    async fetchDetail() {
      if (!this.routeId) {
        return;
      }
      this.loading = true;
      this.addressLabel = "";
      try {
        const res = await this.$http({
          method: "get",
          url: "xuanfeidata",
          data: { id: this.routeId },
        });
        this.detail = res && res.data ? res.data : null;
        await this.resolveDetailCity();
      } catch (error) {
        this.detail = null;
      } finally {
        this.loading = false;
      }
    },
    async resolveDetailCity() {
      const classId = Number(this.detail && this.detail.class_id);
      if (!Number.isFinite(classId) || classId < 1) {
        return;
      }
      try {
        const res = await this.$http({
          method: "get",
          url: "address_list",
        });
        const list = Array.isArray(res && res.data) ? res.data : getListPayload(res && res.data);
        const row = (list || []).find((item) => Number(item.id) === classId);
        this.addressLabel = resolveAddressLabel(row, this.locale);
      } catch (error) {
        this.addressLabel = "";
      }
    },
    async syncLikedState() {
      if (!localStorage.getItem("token")) {
        this.liked = false;
        return;
      }
      const res = await this.$http({
        method: "get",
        url: "xuanfei_favorite_ids",
      });
      const ids = Array.isArray(res && res.data) ? res.data : [];
      this.liked = ids.map((item) => Number(item)).includes(this.routeId);
    },
    async toggleFavorite() {
      if (!localStorage.getItem("token")) {
        this.$toast(this.copy.loginFirst);
        this.$router.push("/Login");
        return;
      }

      const res = await this.$http({
        method: "post",
        url: "xuanfei_favorite_toggle",
        data: {
          id: this.routeId,
          name: this.profileName,
          role: this.profileRole,
          city: this.profileCity,
          public_id: this.publicId,
          image: this.gallery[0] || "",
          about: this.aboutText,
        },
      });

      if (res && res.code === 200) {
        this.liked = !!(res.data && res.data.liked);
        this.$toast(
          this.liked
            ? this.copy.favoriteSuccess || "心动收藏成功"
            : this.copy.favoriteRemoved || "已取消心动"
        );
        return;
      }

      if (res && res.msg) {
        this.$toast(res.msg);
      }
    },
    async copyProfileId() {
      const text = this.publicId;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const input = document.createElement("input");
          input.value = text;
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        this.$toast(this.copy.copied);
      } catch (error) {
        this.$toast(text);
      }
    },
    openService() {
      // Product rule:「立即与她约会」只引导联系专属客服
      if (openKefuContact(this)) {
        return;
      }
      this.serviceVisible = true;
    },
    openAgent(link) {
      openKefuContact(this, { url: link });
    },
  },
};
</script>

<style lang="less" scoped>
.profile-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 50% 0, rgba(165, 92, 255, 0.14), transparent 28%),
    linear-gradient(180deg, #130916 0%, #08050e 100%);
  color: #fff;
}

.profile-shell {
  width: 100%;
  max-width: 23.4375rem;
  margin: 0 auto;
  padding-bottom: 7rem;
}

.hero {
  position: relative;
}

.hero-swipe {
  height: 31.5rem;
  border-radius: 0 0 1.6rem 1.6rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.04);
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.back-btn,
.count-chip {
  position: absolute;
  top: 0.875rem;
  z-index: 2;
}

.back-btn {
  left: 0.875rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 0;
  border-radius: 999px;
  background: rgba(31, 20, 36, 0.62);
  color: #fff;
  backdrop-filter: blur(0.75rem);
  -webkit-backdrop-filter: blur(0.75rem);
}

.back-btn svg {
  width: 1.125rem;
  height: 1.125rem;
}

.count-chip {
  right: 0.875rem;
  display: inline-flex;
  align-items: center;
  height: 2rem;
  padding: 0 0.8rem;
  border-radius: 999px;
  background: rgba(31, 20, 36, 0.7);
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.78rem;
  font-weight: 700;
}

.detail-card {
  position: relative;
  z-index: 3;
  margin: -1.5rem 0.75rem 0;
  padding: 1.15rem 0.95rem 1.2rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.4rem;
  background: linear-gradient(180deg, rgba(8, 5, 14, 0.98), rgba(12, 7, 18, 0.98));
  box-shadow: 0 1.25rem 3rem rgba(0, 0, 0, 0.34);
}

.title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.9rem;
}

.title-copy h1 {
  margin: 0;
  font-size: 2rem;
  line-height: 1.05;
  font-weight: 900;
}

.title-copy p {
  margin: 0.5rem 0 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.9rem;
  font-weight: 600;
}

.title-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.55rem;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 4.9rem;
  height: 1.8rem;
  padding: 0 0.7rem;
  border: 1px solid rgba(70, 232, 168, 0.22);
  border-radius: 999px;
  background: rgba(12, 27, 27, 0.92);
  color: #f8ffff;
  font-size: 0.75rem;
  font-weight: 700;
}

.status-chip i {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: #46e8a8;
  box-shadow: 0 0 0.5rem rgba(70, 232, 168, 0.85);
}

.pid-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 1.95rem;
  padding: 0 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  background: rgba(19, 12, 28, 0.92);
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.76rem;
  font-weight: 700;
}

.pid-chip b {
  color: #fff;
}

.pid-chip svg {
  width: 0.95rem;
  height: 0.95rem;
  color: #ff6e9b;
}

.score-card {
  display: flex;
  gap: 0.9rem;
  align-items: center;
  margin-top: 1.2rem;
  padding: 0.95rem;
  border: 1px solid rgba(255, 103, 155, 0.18);
  border-radius: 1.15rem;
  background: linear-gradient(135deg, rgba(47, 17, 53, 0.82), rgba(20, 12, 34, 0.88));
}

.score-ring {
  --score-angle: 288deg;
  flex-shrink: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 4.85rem;
  height: 4.85rem;
  border-radius: 50%;
  background:
    radial-gradient(circle at center, #1b1027 60%, transparent 61%),
    conic-gradient(#ff4f8f 0 var(--score-angle), rgba(255, 255, 255, 0.12) var(--score-angle) 360deg);
}

.score-ring::after {
  content: "";
  position: absolute;
  inset: 0.38rem;
  border-radius: 50%;
  background: #130a1d;
}

.score-ring strong,
.score-ring span {
  position: relative;
  z-index: 1;
}

.score-ring strong {
  font-size: 1.7rem;
  line-height: 1;
  font-weight: 900;
}

.score-ring span {
  margin-top: 0.2rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.58rem;
  font-weight: 700;
}

.score-copy {
  min-width: 0;
  flex: 1;
}

.score-copy b {
  display: block;
  font-size: 1.05rem;
}

.score-copy p {
  margin: 0.45rem 0 0.75rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.76rem;
  line-height: 1.5;
}

.score-meter {
  height: 0.3rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.score-meter i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #ff4d8f, #ff7fb4);
}

.info-list {
  margin-top: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.2rem;
  overflow: hidden;
  background: rgba(18, 11, 26, 0.96);
}

.info-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 0.85rem;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: transparent;
  color: #fff;
  text-align: left;
}

.info-row:last-child {
  border-bottom: 0;
}

.info-ico {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.7rem;
  background: linear-gradient(135deg, rgba(255, 95, 147, 0.28), rgba(165, 92, 255, 0.2));
  color: #ff6e9b;
  font-size: 0.9rem;
  font-weight: 800;
}

.info-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
}

.info-copy small {
  color: rgba(255, 255, 255, 0.48);
  font-size: 0.68rem;
}

.info-copy b {
  font-size: 0.92rem;
  line-height: 1.3;
}

.info-row em {
  color: #ff5d94;
  font-size: 0.76rem;
  font-style: normal;
  font-weight: 700;
}

.about-card {
  margin-top: 1rem;
  padding: 1rem 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.2rem;
  background: rgba(13, 8, 20, 0.94);
}

.about-card h2 {
  margin: 0 0 0.7rem;
  font-size: 1.05rem;
}

.about-card p {
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  line-height: 1.65;
}

.action-bar {
  position: sticky;
  bottom: calc(5.2rem + env(safe-area-inset-bottom));
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-top: 1rem;
}

.like-btn {
  flex-shrink: 0;
  width: 3.2rem;
  height: 3.2rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(19, 12, 28, 0.96);
  color: rgba(255, 255, 255, 0.82);
}

.like-btn.on {
  color: #ff699a;
}

.like-btn svg {
  width: 1.35rem;
  height: 1.35rem;
}

.book-btn {
  flex: 1;
  height: 3.2rem;
  border: 0;
  border-radius: 1rem;
  background: linear-gradient(135deg, #ff6da3, #ff417f);
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  box-shadow: 0 0.85rem 2rem rgba(255, 82, 139, 0.36);
}

.service-popup {
  width: min(20rem, calc(100% - 2rem));
  border-radius: 1.5rem;
  overflow: hidden;
  background: transparent;
}

.service-card {
  position: relative;
  padding: 1.45rem 1rem 1rem;
  background: linear-gradient(180deg, rgba(32, 17, 45, 0.98), rgba(15, 10, 24, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.service-close {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.2rem;
}

.service-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  margin: 0 auto;
  border-radius: 1rem;
  background: linear-gradient(180deg, #48b6ff, #1696e8);
  color: #fff;
}

.service-icon svg {
  width: 1.65rem;
  height: 1.65rem;
}

.service-card h3 {
  margin: 0.9rem 0 0;
  text-align: center;
  font-size: 1.55rem;
}

.service-sub {
  margin: 0.6rem 0 1rem;
  color: rgba(255, 255, 255, 0.72);
  text-align: center;
  font-size: 0.8rem;
  line-height: 1.6;
}

.service-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-top: 0.6rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 88, 146, 0.3);
  border-radius: 1rem;
  background: rgba(23, 14, 34, 0.9);
  color: #fff;
  text-align: left;
}

.service-item-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.55rem;
  height: 2.55rem;
  border-radius: 0.85rem;
  background: linear-gradient(180deg, #48b6ff, #1696e8);
}

.service-item-icon svg {
  width: 1.35rem;
  height: 1.35rem;
}

.service-item-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.service-item-copy b {
  font-size: 0.95rem;
}

.service-item-copy small {
  color: #6bf4b4;
  font-size: 0.72rem;
}

.service-item em {
  color: #ff6e9b;
  font-size: 0.8rem;
  font-style: normal;
  font-weight: 700;
}

.service-arrow {
  color: rgba(255, 255, 255, 0.72);
  font-size: 1.2rem;
}

.later-btn {
  width: 100%;
  height: 2.85rem;
  margin-top: 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.95rem;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.9rem;
  font-weight: 700;
}

button {
  cursor: pointer;
}

@media (max-width: 47.9375rem) {
  .profile-shell {
    max-width: 24.25rem;
  }

  .hero-swipe {
    height: 31rem;
  }
}

@media (max-width: 22.5rem) {
  .profile-shell {
    max-width: 23.5rem;
  }

  .hero-swipe {
    height: 29rem;
  }

  .detail-card {
    margin-left: 0.55rem;
    margin-right: 0.55rem;
    padding-left: 0.8rem;
    padding-right: 0.8rem;
  }

  .title-copy h1 {
    font-size: 1.8rem;
  }

  .score-card {
    gap: 0.7rem;
  }

  .score-ring {
    width: 4.35rem;
    height: 4.35rem;
  }
}

@media (min-width: 48rem) {
  .profile-shell {
    max-width: 25rem;
  }

  .hero-swipe {
    height: 30.5rem;
  }
}
</style>
